import React, { useEffect, useMemo, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Modal, Toast, ToastContainer } from "react-bootstrap";
import { FaCalendarAlt, FaClipboardList, FaExclamationTriangle, FaPlusCircle, FaSave, FaTimes, FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import "./KHNgay.css";

const fmtDate = (d) => d.toISOString().slice(0, 10);
const parseDate = (s) => {
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const pageStyle = {
  minHeight: "100vh",
  background: "#f0f4f8",
  color: "#1a202c",
  padding: "2rem 0",
};

export default function KHNgay() {
  const [maNV, setMaNV] = useState(() => {
    return localStorage.getItem("MaNV") || "";
  });
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [missingDate, setMissingDate] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [tasks, setTasks] = useState([
    { MaMDCV: "I", STT: "", CongViec: "", TyLeHT: "", DeXuat: "" }
  ]);
  const [toasts, setToasts] = useState([]);

  const filteredRecords = useMemo(() => {
    if (!month) return [];
    return records
      .map((r) => ({ ...r, NgayObj: parseDate(r.Ngay) }))
      .filter((r) => r.NgayObj && fmtDate(r.NgayObj).startsWith(month))
      .sort((a, b) => a.NgayObj - b.NgayObj || (a.STT || 0) - (b.STT || 0));
  }, [records, month]);

  const missingDates = useMemo(() => {
    if (!month) return [];
    const [y, m] = month.split("-").map(Number);
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 0).getDate();
    
    // Chỉ lấy các records có Ngay thuộc tháng được chọn
    const recordsInMonth = filteredRecords.filter(r => {
      const ngayStr = fmtDate(r.NgayObj);
      return ngayStr.startsWith(month);
    });
    
    const grouped = recordsInMonth.reduce((acc, r) => {
      const key = fmtDate(r.NgayObj);
      acc[key] = acc[key] || [];
      acc[key].push(r);
      return acc;
    }, {});
    
    const result = [];
    for (let d = 1; d <= end; d++) {
      const dateStr = fmtDate(new Date(y, m - 1, d));
      const list = grouped[dateStr] || [];
      
      // Chỉ kiểm tra xem có ít nhất 1 công việc mức I và 1 công việc mức II
      const hasPriorityI = list.some((i) => (i.MaMDCV || "").toUpperCase() === "I");
      const hasPriorityII = list.some((i) => (i.MaMDCV || "").toUpperCase() === "II");
      
      // Ngày thiếu báo cáo nếu không có công việc HOẶC thiếu mức I HOẶC thiếu mức II
      if (list.length === 0 || !hasPriorityI || !hasPriorityII) {
        result.push(dateStr);
      }
    }
    return result;
  }, [filteredRecords, month]);

  const groupedByDate = useMemo(() => {
    const grouped = {};
    filteredRecords.forEach((r) => {
      const dateKey = fmtDate(r.NgayObj);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(r);
    });
    return grouped;
  }, [filteredRecords]);

  const addToast = (message, type = "info") => {
    const id = Date.now();
    setToasts([{ id, message, type }]); // Replace all toasts with new one
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000); // Reduced from 5000 to 3000ms
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  useEffect(() => {
    const storedMaNV = localStorage.getItem("MaNV");
    if (storedMaNV && !maNV) {
      setMaNV(storedMaNV);
    }
  }, []);

  useEffect(() => {
    if (!maNV) return;
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`https://doticom.vn:3000/api/ke-hoach-ngay?MaNV=${encodeURIComponent(maNV)}`);
        const json = await res.json();
        if (!json.success) throw new Error(json.message || "Lỗi tải dữ liệu");
        setRecords(json.data || []);
        addToast("Tải dữ liệu thành công", "success");
      } catch (e) {
        const errMsg = e.message || "Lỗi không xác định";
        setError(errMsg);
        addToast(errMsg, "danger");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [maNV]);

  const handleAddTask = () => {
    setTasks(prev => [...prev, { MaMDCV: "I", STT: "", CongViec: "", TyLeHT: "", DeXuat: "" }]);
  };

  const handleRemoveTask = (index) => {
    if (tasks.length > 1) {
      setTasks(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleTaskChange = (index, field, value) => {
    setTasks(prev => prev.map((task, i) => i === index ? { ...task, [field]: value } : task));
  };

  const handleOpenModal = (date) => {
    setMissingDate(date);
    setTasks([
      { MaMDCV: "I", STT: "", CongViec: "", TyLeHT: "", DeXuat: "" },
      { MaMDCV: "II", STT: "", CongViec: "", TyLeHT: "", DeXuat: "" }
    ]);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setMissingDate("");
    setTasks([{ MaMDCV: "I", STT: "", CongViec: "", TyLeHT: "", DeXuat: "" }]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!missingDate) {
      addToast("Chọn ngày cần báo cáo bù", "warning");
      return;
    }
    if (!maNV) {
      addToast("Nhập mã nhân viên", "warning");
      return;
    }

    // Validate tasks
    const validTasks = tasks.filter(t => t.CongViec.trim());
    if (validTasks.length === 0) {
      addToast("Cần ít nhất 1 công việc", "warning");
      return;
    }

    const hasPriorityI = validTasks.some(t => t.MaMDCV === "I");
    const hasPriorityII = validTasks.some(t => t.MaMDCV === "II");
    
    if (!hasPriorityI || !hasPriorityII) {
      addToast("Cần ít nhất 1 công việc mức I và 1 công việc mức II", "warning");
      return;
    }

    // Validate completion rates
    for (const task of validTasks) {
      if (task.TyLeHT !== "") {
        const tyLe = Number(task.TyLeHT);
        if (isNaN(tyLe) || tyLe < 0 || tyLe > 100) {
          addToast("Tỷ lệ hoàn thành phải từ 0-100", "warning");
          return;
        }
      }
    }

    const ngayBaoCao = parseDate(missingDate);
    if (!ngayBaoCao) {
      addToast("Ngày báo cáo không hợp lệ", "danger");
      return;
    }
    
    const ngay = new Date(ngayBaoCao);
    ngay.setDate(ngay.getDate() - 1);

    try {
      setLoading(true);
      const newRecords = [];
      
      for (const task of validTasks) {
        const res = await fetch("https://doticom.vn:3000/api/ke-hoach-ngay", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            MaNV: maNV,
            MaMDCV: task.MaMDCV || "",
            STT: task.STT || "",
            CongViec: task.CongViec,
            TyLeHT: task.TyLeHT === "" || task.TyLeHT == null? 0: Number(task.TyLeHT),
            DeXuat: task.DeXuat || "",
            GhiChu: "",
            Ngay: fmtDate(ngay),
            Nhom: 0,
            RowH: 420,
            NgayBaoCao: fmtDate(ngayBaoCao),
            TapTin: null,
          }),
        });
        const json = await res.json();
        if (!json.success) throw new Error(json.message || "Lỗi lưu báo cáo");
        newRecords.push(json.data);
      }
      
      setRecords(prev => [...prev, ...newRecords]);
      addToast(`Đã lưu thành công ${validTasks.length} công việc`, "success");
      handleCloseModal();
    } catch (e) {
      const errMsg = e.message || "Lỗi không xác định";
      addToast(errMsg, "danger");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wrapper">
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        {toasts.map(toast => (
          <Toast 
            key={toast.id} 
            onClose={() => removeToast(toast.id)}
            bg={toast.type}
            autohide
            delay={3000}
          >
            <Toast.Header>
              <strong className="me-auto">
                {toast.type === "success" && <FaCheckCircle className="me-2" />}
                {toast.type === "danger" && <FaTimesCircle className="me-2" />}
                {toast.type === "warning" && <FaExclamationTriangle className="me-2" />}
                Thông báo
              </strong>
            </Toast.Header>
            <Toast.Body className={toast.type === "danger" || toast.type === "success" ? "text-white" : ""}>
              {toast.message}
            </Toast.Body>
          </Toast>
        ))}
      </ToastContainer>

      <div className="container-fluid px-4" style={{ maxWidth: "1600px" }}>
        <div className="hero-section">
          <h2><FaClipboardList className="me-2" />Kế hoạch công việc</h2>
          <p>Quản lý và theo dõi tiến độ công việc hàng ngày</p>
        </div>

        <div className="row g-4">
          <div className="col-12 col-lg-8">
            <div className="modern-card mb-4">
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="modern-label">Mã nhân viên</label>
                    <input 
                      className="form-control modern-input" 
                      value={maNV} 
                      onChange={(e) => setMaNV(e.target.value.trim())} 
                      placeholder="Nhập mã nhân viên..." 
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <label className="modern-label">Chọn tháng</label>
                    <input 
                      className="form-control modern-input" 
                      type="month" 
                      value={month} 
                      onChange={(e) => setMonth(e.target.value)} 
                    />
                  </div>
                </div>
                {error && <div className="alert alert-danger alert-modern mt-3 mb-0">{error}</div>}
                {loading && <div className="alert alert-info alert-modern mt-3 mb-0">Đang tải dữ liệu...</div>}
              </div>
            </div>

            <div className="modern-card">
              <div className="card-header">
                <FaClipboardList className="me-2" />Danh sách công việc ({filteredRecords.length})
              </div>
              <div className="card-body">
                {Object.keys(groupedByDate).length > 0 ? (
                  Object.keys(groupedByDate)
                    .sort()
                    .map((dateKey) => (
                      <div key={dateKey} className="date-group">
                        <div className="date-header">
                          <FaCalendarAlt className="date-header-icon" />
                          <span>{dateKey}</span>
                        </div>
                        <div className="task-list">
                          {groupedByDate[dateKey].map((task) => (
                            <div key={task.ID} className="task-item">
                              <div className="task-row">
                                <div className="task-stt">
                                  #{task.STT || '-'}
                                </div>
                                <div className="task-priority">
                                  <span className={`priority-badge priority-${task.MaMDCV}`}>
                                    {task.MaMDCV}
                                  </span>
                                </div>
                                <div className="task-content">
                                  {task.CongViec}
                                </div>
                                <div className="task-progress">
                                  {task.TyLeHT ? (
                                    <div className="progress-circle">
                                      {task.TyLeHT}%
                                    </div>
                                  ) : (
                                    <span className="text-muted">-</span>
                                  )}
                                </div>
                                <div className="task-suggestion">
                                  {task.DeXuat || 'Không có đề xuất'}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="empty-state">
                    Chưa có dữ liệu công việc
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-4">
            <div className="modern-card mb-4">
              <div className="card-header">
                <FaExclamationTriangle className="me-2" />Ngày thiếu báo cáo ({missingDates.length})
              </div>
              <div className="card-body">
                <div className="d-flex flex-wrap gap-2">
                  {missingDates.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => handleOpenModal(d)}
                      className="missing-date-chip"
                    >
                      {d}
                    </button>
                  ))}
                  {!missingDates.length && (
                    <div className="w-100 text-center py-3" style={{ color: '#a0aec0' }}>
                      <FaCheckCircle className="me-2" />Đã đủ báo cáo
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaPlusCircle className="me-2" />Thêm báo cáo bù - {missingDate}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleSubmit}>
            <div className="alert alert-info mb-3">
              <FaExclamationTriangle className="me-2" />
              Yêu cầu: Tối thiểu 1 công việc mức I và 1 công việc mức II
            </div>
            
            {tasks.map((task, index) => (
              <div key={index} className="task-form-group">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Công việc {index + 1}</h6>
                  {tasks.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => handleRemoveTask(index)}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-md-6">
                    <label className="modern-label">Mức độ ưu tiên</label>
                    <select 
                      className="form-select modern-input" 
                      value={task.MaMDCV} 
                      onChange={(e) => handleTaskChange(index, "MaMDCV", e.target.value)}
                    >
                      <option value="I">I - Cao nhất</option>
                      <option value="II">II - Cao</option>
                      <option value="III">III - Trung bình</option>
                      <option value="IV">IV - Thấp</option>
                    </select>
                  </div>

                  <div className="col-md-6">
                    <label className="modern-label">Số thứ tự</label>
                    <input 
                      className="form-control modern-input" 
                      value={task.STT} 
                      onChange={(e) => handleTaskChange(index, "STT", e.target.value)}
                      placeholder="Tùy chọn..."
                    />
                  </div>

                  <div className="col-12">
                    <label className="modern-label">Nội dung công việc *</label>
                    <textarea 
                      className="form-control modern-input" 
                      rows="2"
                      value={task.CongViec} 
                      onChange={(e) => handleTaskChange(index, "CongViec", e.target.value)}
                      placeholder="Mô tả chi tiết công việc..."
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="modern-label">Tỷ lệ hoàn thành (%)</label>
                    <input 
                      className="form-control modern-input" 
                      type="number" 
                      min="0" 
                      max="100" 
                      value={task.TyLeHT} 
                      onChange={(e) => handleTaskChange(index, "TyLeHT", e.target.value)}
                      placeholder="0-100"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="modern-label">Đề xuất / Ghi chú</label>
                    <input 
                      className="form-control modern-input" 
                      value={task.DeXuat} 
                      onChange={(e) => handleTaskChange(index, "DeXuat", e.target.value)}
                      placeholder="Đề xuất..."
                    />
                  </div>
                </div>
              </div>
            ))}

            <button 
              type="button" 
              className="btn btn-outline-primary w-100 mb-3"
              onClick={handleAddTask}
            >
              <FaPlusCircle className="me-2" />Thêm công việc
            </button>

            <div className="d-flex gap-2">
              <button 
                type="button" 
                className="btn btn-secondary flex-fill"
                onClick={handleCloseModal}
              >
                Hủy
              </button>
              <button 
                type="submit" 
                className="btn modern-btn flex-fill" 
                disabled={loading}
              >
                {loading ? "Đang lưu..." : <><FaSave className="me-2" />Lưu báo cáo</>}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </div>
  );
}